import React, { useState, useEffect } from 'react';
import { Edit, Trash, Plus, Copy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import UploadDefaultsContainer from './UploadDefaultsContainer.tsx';
import {
  deleteUploadDefault,
  createUploadDefault,
  updateUploadDefault,
  UploadDefault as UploadDefaultDto
} from '../../services/uploadDefaultsService.ts';
import { usePermissions } from '../../hooks/usePermissions';
import { useUploadDefaults } from '../../contexts/AppDataContext';
import './UploadDefaults.css';

export interface UploadDefault {
  id?: number;
  name: string;
  video_title?: string;
  video_description?: string;
  tags?: string[];
  category?: string;
  category_name?: string;
  playlist_id?: string;
  playlist_name?: string;
  privacy_status?: string;
  license?: string;
  made_for_kids?: boolean;
  embeddable?: boolean;
  scheduled?: boolean;
  disable_watermark?: boolean;
}

const defaultUpload: UploadDefault = {
  name: '',
  video_title: '',
  tags: [],
  category: '10',
  privacy_status: 'private',
  embeddable: true,
  license: 'youtube',
  video_description: '',
  playlist_id: '',
  made_for_kids: false,
  scheduled: false,
  disable_watermark: true,
};

const UploadDefaults: React.FC = () => {
  const navigate = useNavigate();
  const { hasFeature, isLoading } = usePermissions();
  const { uploadDefaults, fetchUploadDefaults } = useUploadDefaults();
  const defaults = uploadDefaults ?? [];
  const [selected, setSelected] = useState<UploadDefault | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isLoading && !hasFeature('upload_defaults')) {
      // Redirect to subscriptions page if user doesn't have access
      navigate('/subscriptions', { replace: true });
    }
  }, [hasFeature, isLoading, navigate]);

  useEffect(() => {
    if (hasFeature('upload_defaults') && uploadDefaults === null) {
      fetchUploadDefaults();
    }
  }, [hasFeature, uploadDefaults, fetchUploadDefaults]);

  const handleSave = async (values: UploadDefault) => {
    try {
      const dto: UploadDefaultDto = { ...values };
      if (values.id) {
        await updateUploadDefault(dto);
      } else {
        await createUploadDefault(dto);
      }
      setOpen(false);
      setSelected(null);
      fetchUploadDefaults();
    } catch (err) {
      console.error('Failed to save upload default:', err);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteUploadDefault(id);
      fetchUploadDefaults();
    } catch (err) {
      console.error('Failed to delete default:', err);
    }
  };

  const handleClone = async (original: UploadDefault) => {
    const existingNames = new Set(defaults.map((d) => d.name));
    const base = `${original.name} (Copy)`;
    let name = base;
    let counter = 1;
    while (existingNames.has(name)) {
      name = `${base}(${counter})`;
      counter++;
    }

    try {
      await createUploadDefault({
        ...original,
        id: undefined,
        name,
      });
      fetchUploadDefaults();
    } catch (err) {
      console.error('Failed to clone default:', err);
    }
  };

  // Don't render if user doesn't have permission (will redirect)
  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  if (!hasFeature('upload_defaults')) {
    return null;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-end">
        <Button onClick={() => { setSelected(defaultUpload); setOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" />
          New Default
        </Button>
      </div>

      {defaults.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No upload defaults yet.</p>
              <Button variant="outline" onClick={() => { setSelected(defaultUpload); setOpen(true); }}>
                <Plus className="mr-2 h-4 w-4" />
                Create your first default
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="upload-defaults-wrapper">
          {defaults.map((d) => (
            <Card key={d.id} className="upload-default-card">
              <CardHeader>
                <CardTitle className="upload-default-title">{d.name || 'Untitled Default'}</CardTitle>
              </CardHeader>
              <CardContent className="card-content">
                <div className="upload-default-grid">
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Video Title:</span>
                    <p className="text-sm">{d.video_title || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Category:</span>
                    <p className="text-sm">{d.category_name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Description:</span>
                    <p className="text-sm line-clamp-2">{d.video_description || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Privacy:</span>
                    <Badge 
                      variant="outline" 
                      className="ml-2 border-purple-500/50 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20"
                    >
                      {d.privacy_status || 'private'}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">License:</span>
                    <Badge 
                      variant="outline" 
                      className="ml-2 border-purple-500/50 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20"
                    >
                      {d.license || 'youtube'}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Playlist:</span>
                    <p className="text-sm">{d.playlist_name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Made for Kids:</span>
                    <Badge
                      variant="outline"
                      className="ml-2 border-purple-500/50 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20"
                    >
                      {d.made_for_kids ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Remove Watermark:</span>
                    <Badge
                      variant="outline"
                      className="ml-2 border-purple-500/50 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20"
                    >
                      {d.disable_watermark !== false ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                  {(d.tags || []).length > 0 && (
                    <div className="col-span-2">
                      <span className="text-sm font-medium text-muted-foreground">Tags:</span>
                      <div className="tags-wrapper mt-2">
                        {(d.tags || []).map((tag, i) => (
                          <Badge 
                            key={i} 
                            variant="outline" 
                            className="upload-default-tag border-purple-500/50 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 rounded-full"
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
              <CardFooter className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setSelected(d); setOpen(true); }}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleClone(d)}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Clone
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => d.id && handleDelete(d.id)}
                >
                  <Trash className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selected?.id ? 'Edit Default' : 'Create Upload Default'}</DialogTitle>
            <DialogDescription>
              {selected?.id ? 'Update your upload default settings.' : 'Create a new upload default to use as a template for your uploads.'}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <UploadDefaultsContainer
              values={selected}
              onChange={(updates) => setSelected(prev => prev ? { ...prev, ...updates } : prev)}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => selected && handleSave(selected)}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UploadDefaults;